<template>
  <div class="occupation-field">
    <FormSelect
      name="occupationDropdown"
      label="Select your occupation"
      type="text"
      placeholder="Your occupation"
      :items="occupationList"
      @dropdownValueUpdate="updateOccupationDropdown"
    />

    <FormInputField
      v-if="showFieldToEnterOccupation"
      name="occupationOther"
      label="Please specify*"
      placeholder="Enter your occupation"
      type="text"
      :inputValue="occupationData.occupationOther"
      v-model:occupationOther="occupationData.occupationOther"
      :errors="occupationStore.getErrorByField('occupationOther')"
    />
  </div>
</template>

<script setup>

import FormSelect from "@/components/ui/selects/FormSelect.vue";
import FormInputField from "@/components/ui/inputs/FormInputField.vue";
import baseFormValidators from "@/composables/validations/baseFormValidators.js";
import { useOccupationStore } from "@/stores/forms/occupationFormStore";
import { computed, ref, watch } from "vue";
import useValidate from "@vuelidate/core";
import { required, helpers, requiredIf } from "@vuelidate/validators";

const emit = defineEmits(["inputValueUpdate"]);
const occupationStore = useOccupationStore();
const { isEmpty } = baseFormValidators();

const occupationList = [
  "Accounting",
  "Agriculture",
  "Administration",
  "Arts",
  "Banking",
  "Business Management",
  "Computers or Information Technology",
  "Construction",
  "Education & Training",
  "Entertainment",
  "Finance",
  "Government Official",
  "Healthcare",
  "Hospitality",
  "Human Resources",
  "Insurance",
  "Law",
  "Manufacturing",
  "Marketing",
  "Mining",
  "Media",
  "Other",
  "Real Estate",
  "Retired",
  "Sales",
  "Science",
  "Sports",
  "Student",
  "Transportation",
  "Unemployed",
];

const occupationData = ref({
  occupationDropdown: occupationStore.form.occupationDropdown.value || "",
  occupationOther: occupationStore.form.occupationOther.value || "",
});

// Only characters, space or underscore is allowed
const mustNotContainSpecialCharacters = (value) => /^[A-Za-z0-9 _]*$/.test(value);

// Displaying a field to specify the occupation only if a user has selected 'Other' as an Occupation
const showFieldToEnterOccupation = computed(() => {
  const hasOtherSelected = occupationData.value.occupationDropdown === 'Other';
  return hasOtherSelected;
});

const rules = computed(() => {
  return {
    occupationDropdown: { required },
    occupationOther: {
      required: requiredIf(showFieldToEnterOccupation),
      mustNotContainSpecialCharacters: helpers.withMessage(
        'Must not contain special characters',
        mustNotContainSpecialCharacters,
      ),
    },
  }
});

const v$ = useValidate(rules, occupationData);

// Updating Occuptaion dropdown value
const updateOccupationDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  occupationData.value[field] = value;

  occupationStore.updateValue(field, value);

  // Updating Store and pass the value to parent component
  updateOccupationAndEmit();
};

// Composing the value of occupation
const occupation = computed(() => showFieldToEnterOccupation.value
  ? `Other - ${occupationData.value.occupationOther}`
  : occupationData.value.occupationDropdown);

watch(
  () => occupationData.value.occupationOther,
  (occupationOther) => {
    const trimmedValue = occupationOther.trim();
    // Updating value in the store
    occupationStore.updateValue("occupationOther", trimmedValue);
    // Update Occupation value in store and pass the value to parent component
    updateOccupationAndEmit();
  }
);
  
const updateOccupationAndEmit = () => {
  const validation = v$.value;
  validation.$validate();
 
  // Updating errors in Occupation store
  const occupationOtherErrors = validation.occupationOther.$error ? validation.occupationOther.$errors[0].$message : '';
  const occupationDropdownErrors = validation.occupationDropdown.$error ? validation.occupationDropdown.$errors[0].$message : '';
  occupationStore.updateErrorFieldByKey("occupationOther", occupationOtherErrors);
  occupationStore.updateErrorFieldByKey("occupationDropdown", occupationDropdownErrors);
  
  occupationStore.updateValue("occupation", occupation.value);
  emit("inputValueUpdate", {
    "occupation": occupation.value,
  });
};

</script>